import { TextInput } from "react-admin";
import { useFieldLabels } from "../../i18n/hooks";
import { RichTextInput } from "../richText";

export type LangCode = "AZ" | "RU" | "EN";

const LANG_SUFFIXES: LangCode[] = ["AZ", "RU", "EN"];

const hasText = (value: unknown): boolean =>
  typeof value === "string" ? value.trim().length > 0 : value != null && value !== "";

/**
 * Обязательная локализуемая группа: достаточно заполнить базовое поле
 * или версию на любом языке. Бэкенд хранит локали как nullable и сам
 * делает fallback на базовое значение — поэтому требовать все три языка
 * нельзя: иначе скрытые вкладки блокируют сохранение записей.
 */
export const atLeastOneLang =
  (base: string) =>
  (_value: unknown, allValues: Record<string, unknown>): string | undefined => {
    const sources = [base, ...LANG_SUFFIXES.map((lang) => `${base}${lang}`)];
    return sources.some((src) => hasText(allValues?.[src]))
      ? undefined
      : "Заполните хотя бы на одном языке";
  };

type TitleFieldsProps = {
  lang: LangCode;
  titleRequired?: boolean;
};

export const TitleLangFields = ({ lang, titleRequired }: TitleFieldsProps) => {
  const f = useFieldLabels();
  const label =
    lang === "AZ" ? f.titleAz : lang === "RU" ? f.titleRu : f.titleEn;
  return (
    <TextInput
      source={`Title${lang}`}
      label={label}
      fullWidth
      validate={titleRequired ? atLeastOneLang("Title") : undefined}
    />
  );
};

type BodyFieldsProps = {
  lang: LangCode;
  rows?: number;
  rich?: boolean;
  bodyRequired?: boolean;
  helperText?: string;
};

export const BodyLangFields = ({
  lang,
  rows = 6,
  rich = false,
  bodyRequired,
  helperText,
}: BodyFieldsProps) => {
  const f = useFieldLabels();
  const label =
    lang === "AZ"
      ? `${f.descriptionAz} (текст)`
      : lang === "RU"
        ? `${f.descriptionRu} (текст)`
        : `${f.descriptionEn} (текст)`;
  if (rich) {
    return (
      <RichTextInput
        source={`Body${lang}`}
        label={`${label.replace(" (текст)", "")} (HTML)`}
        validate={bodyRequired ? atLeastOneLang("Body") : undefined}
        helperText={helperText}
      />
    );
  }
  return (
    <TextInput
      source={`Body${lang}`}
      label={label}
      fullWidth
      multiline
      rows={rows}
      validate={bodyRequired ? atLeastOneLang("Body") : undefined}
      helperText={helperText}
    />
  );
};

type DescriptionFieldsProps = {
  lang: LangCode;
  rows?: number;
  descriptionRequired?: boolean;
};

export const DescriptionLangFields = ({
  lang,
  rows = 3,
  descriptionRequired,
}: DescriptionFieldsProps) => {
  const f = useFieldLabels();
  const label =
    lang === "AZ"
      ? f.descriptionAz
      : lang === "RU"
        ? f.descriptionRu
        : f.descriptionEn;
  return (
    <TextInput
      source={`Description${lang}`}
      label={label}
      fullWidth
      multiline
      rows={rows}
      validate={descriptionRequired ? atLeastOneLang("Description") : undefined}
    />
  );
};

type SubtitleFieldsProps = { lang: LangCode; rows?: number };

export const SubtitleLangFields = ({ lang, rows = 2 }: SubtitleFieldsProps) => {
  const f = useFieldLabels();
  const label =
    lang === "AZ"
      ? `${f.descriptionAz} (подзаголовок)`
      : lang === "RU"
        ? `${f.descriptionRu} (подзаголовок)`
        : `${f.descriptionEn} (подзаголовок)`;
  return (
    <TextInput source={`Subtitle${lang}`} label={label} fullWidth multiline rows={rows} />
  );
};

type ActionLabelFieldsProps = { lang: LangCode };

export const ActionLabelLangFields = ({ lang }: ActionLabelFieldsProps) => (
  <TextInput
    source={`ActionLabel${lang}`}
    label={lang === "AZ" ? "Кнопка (AZ)" : lang === "RU" ? "Кнопка (RU)" : "Кнопка (EN)"}
    fullWidth
  />
);

type LevelLabelsFieldsProps = { lang: LangCode };

export const LevelLabelsLangFields = ({ lang }: LevelLabelsFieldsProps) => (
  <>
    <TextInput
      source={`Level1Label${lang}`}
      label={`Уровень 1 (${lang})`}
      fullWidth
      defaultValue={lang === "RU" ? "Слабый" : undefined}
    />
    <TextInput
      source={`Level2Label${lang}`}
      label={`Уровень 2 (${lang})`}
      fullWidth
      defaultValue={lang === "RU" ? "Средний" : undefined}
    />
    <TextInput
      source={`Level3Label${lang}`}
      label={`Уровень 3 (${lang})`}
      fullWidth
      defaultValue={lang === "RU" ? "Сильный" : undefined}
    />
  </>
);

type PhraseFieldsProps = { lang: LangCode; phraseRequired?: boolean; rows?: number };

export const PhraseLangFields = ({ lang, phraseRequired, rows = 2 }: PhraseFieldsProps) => {
  const f = useFieldLabels();
  const label =
    lang === "AZ" ? f.phraseAz : lang === "RU" ? f.phraseRu : f.phraseEn;
  return (
    <TextInput
      source={`Phrase${lang}`}
      label={label}
      fullWidth
      multiline
      rows={rows}
      validate={phraseRequired ? atLeastOneLang("Phrase") : undefined}
    />
  );
};
